import { createContext, useContext, useEffect } from 'react';

import {
  useChart as useDefaultChart,
  useChartAtom,
  type Chart,
} from '@cognite/charts-lib';

// Based on useInitializedChart found in the Charts code in Fusion
const useInitializedChart = ({
  id,
  projectId,
}: {
  id: string;
  projectId: string;
}): { data: Chart | undefined; isFetching: boolean; isError: boolean } => {
  const { useChart } = useContext(StaticDependencies);
  const [chart, setChart] = useChartAtom();
  const {
    data: originalChart,
    isFetched,
    isError,
    isFetching,
  } = useChart({ id, projectId, refetchOnWindowFocus: 'always' });

  useEffect(() => {
    if (originalChart === undefined) {
      return;
    }
    setChart(originalChart);
  }, [originalChart, setChart]);

  return {
    data: chart,
    isFetching:
      !isFetched ||
      (isFetched && originalChart !== undefined && chart === undefined) ||
      isFetching,
    isError,
  };
};

export const StaticDependencies = createContext({
  useChart: useDefaultChart as (
    ...args: Parameters<typeof useDefaultChart>
  ) => Pick<
    ReturnType<typeof useDefaultChart>,
    'data' | 'isFetched' | 'isError' | 'isFetching'
  >,
});

export default useInitializedChart;
